import { useMemo } from 'react';

import { State } from 'modules';

import { bindActionCreators, useDispatch, useSelector } from 'third-party';

import { DEFAULT_PAGE_SIZE } from 'constants/common';
import { auditEndpoint } from 'constants/endpoints';

import { appSelectors } from 'selectors';

import { getPageNumberAfterLoadMore } from 'utils/helper';
import { httpMethod, simplifyBuilder } from 'utils/sra';

import {
  APIAudit,
  APIAuditSearchFilter,
  APIAuditSearchResult,
  APISortOrder,
} from 'types/api';

export type AuditState = {
  auditList: APIAudit[];
  loadAuditPending: boolean;
  auditListLoaded: boolean;
  allAuditLoaded: boolean;
  filters: APIAuditSearchFilter;
  pagination: {
    pageNumber: APIAuditSearchResult['pageNumber'];
    pageSize: APIAuditSearchResult['pageSize'];
  };
};

export const auditState: AuditState = {
  auditList: [],
  loadAuditPending: false,
  auditListLoaded: false,
  allAuditLoaded: false,
  filters: {
    searchSortOrders: [
      {
        sortField: 'createdAt',
        sortOrder: APISortOrder.DESC,
      },
    ],
  },
  pagination: { pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE },
};

const builder = simplifyBuilder(auditState, {});

export const loadAudits = builder.createServerAction(
  (tenantId: string) => ({
    name: 'loadAudits',
    url: auditEndpoint(tenantId),
    body: (state: State) => ({
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      ...state.audit.filters,
    }),
    method: httpMethod.post,
    onSuccess: (state: AuditState, payload: APIAuditSearchResult) => ({
      auditList: payload.result,
      auditListLoaded: true,
      allAuditLoaded: payload.result.length < payload.pageSize,
      pagination: {
        pageNumber: payload.pageNumber,
        pageSize: payload.pageSize,
      },
    }),
  }),
);

export const loadMoreAudits = builder.createServerAction(
  (tenantId: string) => ({
    name: 'loadMoreAudits',
    url: auditEndpoint(tenantId),
    body: (state: State) => ({
      pageNumber: getPageNumberAfterLoadMore(
        state.audit.auditList,
        state.audit.pagination.pageSize,
      ),
      ...state.audit.filters,
    }),
    method: httpMethod.post,
    onSuccess: (state: AuditState, payload: APIAuditSearchResult) => ({
      auditList: [...(state.auditList || []), ...payload.result],
      auditListLoaded: true,
      allAuditLoaded: payload.result.length < payload.pageSize,
      pagination: {
        pageNumber: payload.pageNumber,
        pageSize: payload.pageSize,
      },
    }),
  }),
);

const updateAuditFilters = builder.createReduxAction(
  (filters: APIAuditSearchFilter) => ({
    name: 'updateAuditFilters',
    updater: () => ({
      filters,
    }),
  }),
);

export const clearAuditList = builder.createReduxAction(() => ({
  name: 'clearAuditList',
  updater: () => ({
    auditList: [],
  }),
}));

export const useAudit = () => {
  const dispatch = useDispatch();
  const tenantId = useSelector(appSelectors.tenantId);

  return useMemo(
    () =>
      bindActionCreators(
        {
          updateAuditFilters,
          clearAuditList,
          loadAudits: loadAudits.bind(null, tenantId),
          loadMoreAudits: loadMoreAudits.bind(null, tenantId),
        },
        dispatch,
      ),
    [dispatch, tenantId],
  );
};

export const auditReducer = builder.getReducers();
